/**
 * src/services/authService.ts
 * =============================================================================
 * Cliente del flujo de autenticación contra Better Auth en api.ploter.me.
 *
 * Endpoints que usa (mismos que `better-auth/client` consumiría, pero
 * llamados a pelo con fetch para evitar añadir la lib al bundle):
 *  - POST /auth/sign-up/email  → registra user + dispara email de verify
 *  - POST /auth/sign-in/email  → crea sesión (cookie HTTP-only)
 *  - POST /auth/sign-out       → invalida la sesión
 *  - GET  /auth/get-session    → devuelve {user, session} o null
 *  - POST /auth/request-password-reset → manda email para reset
 *
 * Registro y reset llevan la cabecera `x-altcha` (captcha invisible, ver
 * src/lib/captcha.ts): sin ella la API responde 400 `captcha_invalid`.
 *  - POST /auth/reset-password  → cambia password con el token del email
 *
 * Cookies: el navegador maneja la sesión vía cookie HTTP-only que setea
 * Better Auth. `credentials: 'include'` en cada request asegura que viaje.
 * ploter.me ↔ api.ploter.me son same-site (mismo eTLD+1), así que la
 * cookie con SameSite=Lax (default de Better Auth) fluye sin problemas.
 *
 * Tras signup el user queda con `emailVerified=false`. El hook
 * `databaseHooks.user.create.after` del Worker crea profile + organization
 * + membership admin. No requiere RPC del cliente.
 * =============================================================================
 */

import { api } from '@/lib/api'

export interface AuthUser {
  id: string
  email: string
  name: string | null
  emailVerified: boolean
  image: string | null
}

export interface AuthSession {
  id: string
  userId: string
  expiresAt: string
}

export interface SignUpParams {
  email: string
  password: string
  name: string
  /**
   * URL relativa a la que Better Auth redirige tras la verificación del
   * email. Por defecto el Worker la fija a `/app` (que en frontend
   * redirige a `/`). Sobrescribir cuando el user viene de un flow
   * específico — p.ej. invitación nominal: `/invite/accept?token=<token>`.
   */
  callbackURL?: string
}

export interface SignUpResult {
  /** Better Auth siempre exige verificar email antes del primer login. */
  requiresEmailConfirmation: true
}

/** Registra al usuario. Better Auth manda email de verificación automáticamente. */
export async function signUp(params: SignUpParams, captcha: string): Promise<SignUpResult> {
  await api('/auth/sign-up/email', {
    method: 'POST',
    headers: { 'x-altcha': captcha },
    body: {
      email: params.email,
      password: params.password,
      name: params.name,
      ...(params.callbackURL ? { callbackURL: params.callbackURL } : {}),
    },
  })
  return { requiresEmailConfirmation: true }
}

/** Login con email + password. Si el email no está verificado, Better Auth devuelve error. */
export async function signIn(email: string, password: string): Promise<void> {
  await api('/auth/sign-in/email', { method: 'POST', body: { email, password } })
}

/** Cierra la sesión actual (la cookie queda invalidada por el servidor). */
export async function signOut(): Promise<void> {
  await api('/auth/sign-out', { method: 'POST', body: {} })
}

/**
 * Cuánto se espera a saber si hay sesión.
 *
 * Generoso a propósito. Podría parecer que conviene acortarlo para que la
 * aplicación arranque antes, pero eso sería resolver el problema en el sitio
 * equivocado: quien no debe esperar por esto son las pantallas públicas, y eso
 * ya está arreglado donde tocaba (ver `PublicOnlyRoute`).
 *
 * Recortarlo aquí tendría un efecto peor: a alguien con conexión lenta y
 * sesión válida se le trataría como visitante, y en las rutas privadas eso es
 * echarle a la pantalla de entrar por ir mal de cobertura.
 */
const ESPERA_SESION_MS = 12_000

/**
 * Devuelve la sesión activa, o null si no hay cookie / la cookie expiró.
 * Llamada barata (~30ms desde el edge); úsala libremente.
 */
export async function getSession(): Promise<{ user: AuthUser; session: AuthSession } | null> {
  const data = await api<{ user?: AuthUser; session?: AuthSession } | null>(
    '/auth/get-session',
    { signal: AbortSignal.timeout(ESPERA_SESION_MS) },
  )
  if (!data || !data.user || !data.session) return null
  return { user: data.user, session: data.session }
}

/**
 * Dispara email de reset de contraseña — Better Auth genera el token + URL.
 *
 * `redirectTo` es la URL del FRONTEND a la que apunta el link del email.
 * Better Auth la concatena con el token (`${redirectTo}?token=...`). Sin
 * este parámetro, el link apuntaría al endpoint del Worker, no a la página
 * /reset-password del cliente — el usuario vería JSON crudo.
 */
export async function requestPasswordReset(
  email: string,
  redirectTo: string,
  captcha: string,
): Promise<void> {
  // ⚠️ `/request-password-reset`: Better Auth 1.7 ya no tiene `/forget-password`
  // y esta llamada devolvía 404 — "he olvidado mi contraseña" no mandaba
  // ningún correo en producción (encontrado el 2026-10-22).
  await api('/auth/request-password-reset', {
    method: 'POST',
    headers: { 'x-altcha': captcha },
    body: { email, redirectTo },
  })
}

/** Cambia la contraseña con el token recibido por email. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api('/auth/reset-password', {
    method: 'POST',
    body: { token, newPassword },
  })
}
